import React from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Calendar,
  BookOpen,
  Settings,
  LogOut,
  Menu,
  X,
  GraduationCap,
  ClipboardList,
  Target,
  Zap
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { useState } from 'react';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/projects', icon: FolderKanban, label: 'Projects' },
  { path: '/deadlines', icon: Calendar, label: 'Deadlines' },
  { path: '/planner', icon: ClipboardList, label: 'Planner' },
  { path: '/recall', icon: Zap, label: 'Recall' },
  { path: '/research', icon: BookOpen, label: 'Research' },
  { path: '/college', icon: GraduationCap, label: 'Colleges' },
  { path: '/sat-prep', icon: Target, label: 'SAT Prep' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

const AppLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-56 bg-white border-r border-border z-50 hidden md:flex flex-col">
        {/* Logo */}
        <div className="px-5 py-4 border-b border-border">
          <Link to="/hub" className="flex items-center gap-2.5" data-testid="nav-hub-logo">
            <div className="w-4 h-4 bg-primary shrink-0" />
            <span className="font-bold text-[13px] uppercase tracking-[0.1em]">Coursework</span>
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2">
          <p className="label-swiss px-3 pb-2">Navigation</p>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 mb-0.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? 'border-primary text-primary font-medium bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="border-t border-border px-4 py-3">
          <div className="mb-2">
            <p className="text-[12px] font-medium text-foreground truncate">{user?.name}</p>
            <p className="text-[11px] text-muted-foreground truncate font-mono">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[11px] text-muted-foreground hover:text-destructive transition-colors uppercase tracking-wide font-semibold"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border">
        <div className="flex items-center justify-between px-4 h-12">
          <Link to="/hub" className="flex items-center gap-2">
            <div className="w-3.5 h-3.5 bg-primary" />
            <span className="font-bold text-[12px] uppercase tracking-[0.1em]">Coursework</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white pt-12">
          <nav className="p-4 border-b border-border">
            <p className="label-swiss px-3 pb-3">Navigation</p>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-3 mb-0.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? 'border-primary text-primary font-medium bg-primary/5'
                      : 'border-transparent text-muted-foreground'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4">
            <p className="text-[12px] font-medium text-foreground">{user?.name}</p>
            <p className="text-[11px] text-muted-foreground font-mono mb-3">{user?.email}</p>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[11px] text-muted-foreground hover:text-destructive transition-colors uppercase tracking-wide font-semibold"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border">
        <div className="flex">
          {navItems.slice(0, 4).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center py-2.5 gap-1 transition-colors border-t-2 ${
                  isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              <span className="text-[9px] uppercase tracking-wide font-semibold">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="md:ml-56 pt-12 md:pt-0 pb-16 md:pb-0 min-h-screen">
        <div className="p-5 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
